import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding-top: 8rem;
  background: linear-gradient(135deg, #042E4B 0%, #0A4A6F 100%);
  color: white;
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  background-image: radial-gradient(circle, white 1px, transparent 1px);
  background-size: 50px 50px;
`;

const TopLogo = styled(motion.div)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 10;
`;

const TopLogoText = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: white;
  font-family: 'Roboto', sans-serif;
`;

const TopLogoIcon = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: #EF443C;
  font-family: 'Roboto', sans-serif;
`;

const CenterLogo = styled(motion.img)`
  width: 120px;
  height: 120px;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 10px 30px rgba(239, 68, 60, 0.5));
`;

const Logo = styled(motion.div)`
  font-size: 6rem;
  font-weight: 900;
  color: #EF443C;
  text-shadow: 0 10px 30px rgba(239, 68, 60, 0.5);
  margin-bottom: 1.5rem;
  font-family: 'Roboto', sans-serif;
`;

const Tagline = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 300;
  color: white;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 800px;
`;

const IconBadge = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 1.5rem 3rem;
  border-radius: 50px;
  font-size: 1.5rem;
  font-weight: 500;
  border: 2px solid rgba(239, 68, 60, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(239, 68, 60, 0.1),
      transparent
    );
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%) translateY(-100%) rotate(45deg);
    }
    100% {
      transform: translateX(100%) translateY(100%) rotate(45deg);
    }
  }
`;

const Icon = styled.span`
  font-size: 2rem;
`;

const AIBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #EF443C 0%, #ff6b63 100%);
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  font-size: 1.2rem;
  font-weight: 700;
  margin-left: 1rem;
  box-shadow: 0 4px 15px rgba(239, 68, 60, 0.4);
  position: relative;

  &::before {
    content: '✨';
    position: absolute;
    left: -10px;
    animation: sparkle 2s infinite;
  }

  @keyframes sparkle {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.2);
    }
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.8rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2rem;
  font-weight: 300;
`;

const BottomLogos = styled(motion.div)`
  position: absolute;
  bottom: 4rem;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  z-index: 10;
  padding: 2rem 2rem 0 2rem;
`;

const BottomLogoImage = styled.img`
  height: 100px;
  width: auto;
  max-width: 500px;
  object-fit: contain;
  filter: brightness(1.2) contrast(1.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const Slide1Capa: React.FC = () => {
  return (
    <Container>
      <BackgroundPattern />
      
      <TopLogo
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        <TopLogoIcon>T</TopLogoIcon>
        <TopLogoText>ThinkWay</TopLogoText>
      </TopLogo>
      
      <CenterLogo
        src="/logo192.png"
        alt="ThinkWay Logo"
        initial={{ scale: 0.5, opacity: 0, rotate: -10 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
      />
      
      <Logo
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        ThinkWay
      </Logo>

      <Tagline
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.25 }}
      >
        Planejamento Estratégico Descomplicado e na ponta dos dedos
      </Tagline>

      <IconBadge
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.4 }}
      >
        <Icon>🌐</Icon>
        <span>Plataforma 100% Online</span>
        <AIBadge
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ 
            duration: 0.3, 
            delay: 0.6,
            type: "spring",
            stiffness: 200
          }}
        >
          🤖 IA Embarcada
        </AIBadge>
      </IconBadge>

      <BottomLogos
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.55 }}
      >
        <BottomLogoImage src="/logosprojeto.png" alt="Logos Projeto" />
        <BottomLogoImage src="/logonascer.png" alt="Logo Nascer" />
      </BottomLogos>

    </Container>
  );
};

export default Slide1Capa;
